import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../scripts/api';

const SEVERITY_COLORS = { minor: '#d97706', major: '#ef4444', safety: '#7c3aed' };

export default function PdiSelectPage() {
  const nav = useNavigate();
  const [rows,            setRows]            = useState([]);
  const [msg,             setMsg]             = useState('');
  const [busy,            setBusy]            = useState(false);
  const [templates,       setTemplates]       = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [search,          setSearch]          = useState('');
  const [defReport,       setDefReport]       = useState(null);
  const [defBusy,         setDefBusy]         = useState(false);

  useEffect(() => {
    const activeDb = sessionStorage.getItem('active_db') || '';
    if (!activeDb) { setMsg('Please select a database first.'); return; }
    const load = async () => {
      setBusy(true); setMsg('');
      try {
        const [invRes, tplRes] = await Promise.all([
          api.get('/reports/ops', { params: { table: 'all_inventory', preset: 'in_stock_calgary', limit: 500 } }),
          api.get('/pdi/templates'),
        ]);
        setRows(invRes.data.rows || []);
        const tplList = tplRes.data.templates || [];
        setTemplates(tplList);
        if (tplList.length > 0) setSelectedTemplate(tplList[0].template);
      } catch (e) {
        setMsg(`Failed to load: ${e?.response?.data?.error || e.message}`);
      } finally {
        setBusy(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // filter rows by search term
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.rowo, r.stock_po, r.customer, r.unit_status, r.unit_location, r.body_vin, r.chassis_vin, String(r.id)]
        .some(v => v && String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  // when starting PDI: use row's pdi_template if set, else fall back to selected
  const startPdi = (row) => {
    const tpl = row.pdi_template || selectedTemplate;
    nav(`/pdi/${row.id}?template=${encodeURIComponent(tpl)}&table=all_inventory`);
  };

  const viewDeficiencies = async (row) => {
    setDefBusy(true); setDefReport(null);
    try {
      const { data } = await api.get(`/pdi/report/inventory/${row.id}/deficiencies`);
      setDefReport({ inventoryId: row.id, rowo: row.rowo, deficiencies: data.deficiencies || [] });
    } catch (e) {
      setMsg(`Failed to load deficiency report: ${e?.response?.data?.error || e.message}`);
    } finally {
      setDefBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>PDI — In Stock</h2>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={() => nav('/pdi/templates')}>Manage Templates</button>
          <button className="btn-outline" onClick={() => nav('/vin')}>Scan VIN</button>
        </div>
      </div>

      {/* Template selector */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          Default PDI Template
          <span className="muted" style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            (used when a unit has no template assigned)
          </span>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {templates.map(t => (
            <button
              key={t.template}
              className={selectedTemplate === t.template ? 'btn' : 'btn-outline'}
              onClick={() => setSelectedTemplate(t.template)}
            >
              {t.title}
              {t.source === 'db' && <span className="muted" style={{ fontSize: 10, marginLeft: 4 }}>custom</span>}
            </button>
          ))}
          {templates.length === 0 && !busy && (
            <div className="muted">
              No templates found. <button className="btn-outline" style={{ marginLeft: 8 }} onClick={() => nav('/pdi/templates')}>Create one</button>
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div style={{ marginTop: 12 }}>
        <input
          className="input"
          style={{ width: '100%', fontSize: 15 }}
          placeholder="Search by RO/WO, stock, customer, VIN, status, location…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {busy && <div className="muted" style={{ marginTop: 10 }}>Loading…</div>}
      {msg && <div className="muted" style={{ marginTop: 10, color: '#ef4444' }}>{msg}</div>}

      {/* Deficiency report panel */}
      {defReport && (
        <div className="card" style={{ marginTop: 12, border: '2px solid #ef4444' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 700 }}>
              Deficiency Report — Unit #{defReport.inventoryId}
              {defReport.rowo ? ` (RO/WO: ${defReport.rowo})` : ''}
            </div>
            <button className="btn-outline" onClick={() => setDefReport(null)}>Close</button>
          </div>
          {defReport.deficiencies.length === 0 && <div className="muted">No deficiencies on record.</div>}
          {defReport.deficiencies.map(d => (
            <div key={d.id} style={{ borderBottom: '1px solid #eee', padding: '8px 0' }}>
              <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: SEVERITY_COLORS[d.severity] || '#6b7280', textTransform: 'uppercase' }}>{d.severity}</span>
                {d.step_id && <span className="muted" style={{ fontSize: 11 }}>Step {d.step_id}</span>}
                <span style={{ fontWeight: d.status === 'open' ? 600 : 400 }}>{d.description}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 99, background: d.status === 'resolved' ? '#d1fae5' : '#fee2e2', color: d.status === 'resolved' ? '#065f46' : '#991b1b' }}>
                  {d.status}
                </span>
              </div>
              {d.resolution_note && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Resolution: {d.resolution_note}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Unit list */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 13, color: '#6b7280' }}>
          {filtered.length} unit{filtered.length !== 1 ? 's' : ''}
          {search && ` matching "${search}"`}
        </div>
        {filtered.length === 0 && !busy && <div className="muted">No units found.</div>}
        <div className="space-y">
          {filtered.map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, borderBottom: '1px solid #eee', padding: '10px 0', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700 }}>
                  Unit #{r.id}
                  {r.pdi_template && (
                    <span style={{ marginLeft: 8, fontSize: 11, background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                      {r.pdi_template}
                    </span>
                  )}
                </div>
                <div className="muted" style={{ fontSize: 13 }}>
                  RO/WO: {r.rowo || '—'} · Stock: {r.stock_po || '—'} · {r.customer || '—'}
                </div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {r.unit_status || '—'} · {r.unit_location || '—'}
                  {r.body_vin && ` · VIN: ${r.body_vin}`}
                </div>
              </div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <button className="btn" onClick={() => startPdi(r)}>Start PDI</button>
                <button className="btn-outline" onClick={() => viewDeficiencies(r)} disabled={defBusy}>Deficiency Report</button>
                <button className="btn-outline" onClick={() => nav(`/detail/all_inventory/${r.id}`)}>View Record</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
